import React, { useState } from 'react'
import { faBuilding, faIdBadge, faLock, faShieldAlt, faEnvelope, faUser, faPhoneAlt } from '@fortawesome/pro-light-svg-icons'
import { Button, ButtonSizeTypes, ButtonType, Icon, ModalTypes, StandardModal, Tag, TagType } from '@digicert/dcone-common-ui'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useHistory } from 'react-router-dom'
import { formatDate, lastModified, serviceIcon, translate } from '../../../../shared/helpers/utils'
import { RouterConfig } from '../../../../core/utils/constants'
import { statusRender } from 'core/utils/renders.helpers'
import { IAccessScope } from 'reducers/Reducers.interfaces'
import styles from './UserOverview.module.scss'
import { Translate } from 'react-localize-redux'
import { IUserOverview, UserUpdateType } from './UserOverview.types'
import { DIGIROOT_ID } from 'core/constants'
import { disableUserEdit } from 'core/actions/Users.actions'
import EditUserSideRail from '../EditUserSideRail'
import cn from 'classnames'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { Source } from 'secure-pages/groups/Groups.types'
import { ENABLED } from 'core/constants'

const UserOverview = ({ user, deleteUser, account, profile, updateDetails, initLocalization, resetMfaLink, resendVerificationLink }: IUserOverview) => {
  const history = useHistory()
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false)
  const [showUnlockUserModal, setShowUnlockUserModal] = useState(false)
  const [sideRailContent, setSideRailContent] = useState<UserUpdateType>(UserUpdateType.UserOverview)
  const [showSideRail, setShowSideRail] = useState<boolean>(false)
  const isProfileEndUser = profile?.access_scope === IAccessScope.account && !profile.groups.find(group => group.name === 'ol_account_admin')
  const ssoMethodSaml = account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnSaml')
  const standardSignIn = account?.sign_in_methods.find(item => item.signInMethod === 'standard')
  const ssoMethodOidc = account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnOidc')
  const stdSignInEnabled = standardSignIn?.status === ENABLED
  const mfaEnabled = standardSignIn?.mfaStatus === ENABLED
  const cacEnabled = standardSignIn?.clientAuthCertLoginEnabled === true
  const cacEnabled1fa = standardSignIn?.clientAuthCert1faEnabled === true
  const ssoSamlEnabled = ssoMethodSaml?.status === ENABLED
  const oidcEnabled = ssoMethodOidc?.status === ENABLED

  const checkEditUser = () => {
    if (profile) {
      if (!disableUserEdit(user)) {
        if (user?.id === profile.id) {
          return true
        } else {
          if (user?.access_scope === IAccessScope.system) {
            if (user?.id === DIGIROOT_ID) {
              return profile.can_edit_root_user
            } else {
              return profile.can_edit_system_user
            }
          } else {
            return profile.can_edit_account_user
          }
        }
      }
    }
  }

  const canEditActiveUser = () => {
    return checkEditUser() && user?.status === 'ACTIVE'
  }

  const canEditLockedAndActiveUser = () => {
    return checkEditUser() && (user?.status === 'LOCKED' || user?.status === 'ACTIVE')
  }

  const enableSideRail = (updateType: UserUpdateType) => {
    if (canEditActiveUser()) {
      setSideRailContent(updateType)
      setShowSideRail(true)
    }
  }

  const editBasicDetails = () => {
    if(canEditLockedAndActiveUser()) {
      setSideRailContent(UserUpdateType.UserDetails)
      setShowSideRail(true)
    }
  }

  const handleAccountAccessSection = () => {
    if (user?.access_scope === IAccessScope.system && user?.id === profile?.id) {
      history.push(RouterConfig.accounts.table)
    }
    else if (user?.access_scope === IAccessScope.account && user?.id === profile?.id) {
      history.push(RouterConfig.accounts.details(user.accounts[0].id))
    }
    else {
      enableSideRail(UserUpdateType.AccountAccess)
    }
  }

  const enableResetCredentials = () => {
    setSideRailContent(UserUpdateType.UserCredentials)
    setShowSideRail(true)
  }

  const handleServicesSection = (e) => {
    e.preventDefault()
    if (user?.id === profile?.id) {
      history.push(RouterConfig.urlMap.myServices)
    }
    else {
      history.push(RouterConfig.administrators.services(user?.id))
    }
  }

  const unlockUser = () => {
    if (user.can_unlock_with_mfa) {
      resetMfaLink(user.id, user?.status === 'LOCKED', user.user_name,  <Translate id={'notifications.users.unlockUser'} data={{ username: user.user_name }} />).then(() => {
        setShowUnlockUserModal(false)
      })
    } else if (user.can_unlock_with_password) {
      resendVerificationLink(user.id, user?.status === 'LOCKED', <Translate id={'notifications.users.unlockUser'} data={{ username: user.user_name }} />).then(() => {
        setShowUnlockUserModal(false)
      })
    }
  }

  const isScimUser = user?.source === Source.SCIM
  const isSystemUser = profile?.access_scope === IAccessScope.system

  // For non-SCIM users: can delete if not DELETED and not self
  // For SCIM users: account admin cannot delete, system users can delete
  const canDeleteUser = user?.status !== 'DELETED' && user.id !== profile?.id &&
    (user?.access_scope === IAccessScope.system ?
      (user?.id === DIGIROOT_ID ? profile?.can_edit_root_user : profile?.can_edit_system_user) :
      profile?.can_edit_account_user) &&
    (isScimUser ? isSystemUser : true)

  const canEditUser =  user?.access_scope === IAccessScope.system ? profile?.can_edit_system_user : profile?.can_edit_account_user

  return (
    <section className={styles.userOverview}>
      <header className={styles.header}>
        <div id='user-details' className={styles.userInfo}>
          <h1 className={styles.heading}>{user.first_name + ' ' + user.last_name}</h1>
          <div className={styles.pillsRow}>
            {statusRender(user)}
            {isScimUser && (
              <Tag type={TagType.INACTIVE} className={styles.managedByIdPTag}>
                <FontAwesomeIcon icon={faLock as IconProp} />
                <Translate id='accounts.details.scim.managedByIdP' />
              </Tag>
            )}
          </div>
          <p className={styles.id}><Translate id='notifications.users.details.userId' />: {user.id}</p>
          <p className={styles.details}>
            <Translate id='common.createdOn' /> {formatDate(user?.created_at || '')}&nbsp;
            <Translate id='common.at' /> {formatDate(user?.created_at || '', 'h:mm A')}&nbsp;
            <Translate id='common.by' />&nbsp;
            {user.created_by}
            {user.updated_at && user.updated_by && <>
              <span className={styles.separator}>|</span>
              <Translate id='common.lastModified' /> {lastModified(user.updated_at)}&nbsp;
              <Translate id='common.at' /> {formatDate(user.updated_at, 'h:mm A')}&nbsp;
              <Translate id='common.by' />&nbsp;
              {user.updated_by}&nbsp;
            </>}
          </p>
        </div>
        <div id='locked-user' className={styles.actions}>
        {user?.status === 'LOCKED' &&  canEditUser &&
          <Button buttonType={ButtonType.DESTRUCTIVEOUTLINE} size={ButtonSizeTypes.SMALL}
            onClick={() => setShowUnlockUserModal(true)}
          >
            <Translate id='manageUser.administrators.table.unlockUser' />
          </Button>
        }
        {canDeleteUser &&
          <Button buttonType={ButtonType.DESTRUCTIVE} size={ButtonSizeTypes.SMALL}
            onClick={() => setShowDeleteUserModal(true)}
          >
            <Translate id='manageUser.administrators.delete.deleteUser' />
          </Button>
        }
        </div>
      </header>

      <div className={styles.userOverviewOptions}>
        <button className={cn(styles.userOverviewOption, !canEditLockedAndActiveUser() && styles.inActive)} onClick={() => editBasicDetails()}>
          <p className={styles.label}><Translate id='common.userDetails' /></p>
          <div className={styles.items}>
            <div className={styles.item}>
              <FontAwesomeIcon icon={faEnvelope as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='common.form.email' /></p>
                <p className={styles.name}>{user.email}</p>
              </div>
            </div>
            <div className={styles.item}>
              <FontAwesomeIcon icon={faUser as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='common.form.username' /></p>
                <p className={styles.name}>{user.user_name}</p>
              </div>
            </div>

            { (user.oidc_username &&
              account?.sign_in_methods?.find(method=>method.signInMethod=== 'singleSignOnOidc' && method.status === 'enabled')) &&
              (
                <div className={styles.item}>
                  <Icon icon={faUser as IconProp} className={styles.icon} />
                  <div className={styles.info}>
                    <p className={styles.label}><Translate id='common.form.oidcUsername' /></p>
                    <p className={styles.name}>{user.oidc_username}</p>
                  </div>
                </div>
              )
            }

            { (user.saml_username &&
             account?.sign_in_methods?.find(method=>method.signInMethod=== 'singleSignOnSaml' && method.status === 'enabled')) && (
                <div className={styles.item}>
                  <Icon icon={faUser as IconProp} className={styles.icon} />
                  <div className={styles.info}>
                    <p className={styles.label}><Translate id='common.form.samlUsername' /></p>
                    <p className={styles.name}>{user.saml_username}</p>
                  </div>
                </div>
              )
            }

            <div className={styles.item}>
              <FontAwesomeIcon icon={faPhoneAlt as IconProp} className={styles.icon} />
              <div className={styles.info}>
                {user.phone || !user.other_phone ? <p className={styles.label}><Translate id='common.form.phone' /></p> : <p className={styles.label}><Translate id='common.form.otherPhone' /></p>}
                <p className={styles.name}>
                  {user.phone ? user.phone : user.other_phone ? user.other_phone: translate('common.form.notProvided') as string}
                </p>
              </div>
            </div>
          </div>
        </button>

        <button className={cn(styles.userOverviewOption, !canEditActiveUser() && styles.inActive)} onClick={handleAccountAccessSection}>
          <p className={styles.label}><Translate id='manageUser.administrators.form.accountAccess' /></p>
          <div className={styles.items}>
            <div className={styles.item}>
              <FontAwesomeIcon icon={faBuilding as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='notifications.users.details.digicertAccount' /></p>
                <p className={styles.name}>{user.accounts.length > 1 || user.access_scope === IAccessScope.system ? <Translate id='common.form.allAccounts' /> : user.accounts[0]?.display_name}</p>
              </div>
            </div>
            {user.accounts.length === 1 && <div className={styles.item}>
              <FontAwesomeIcon icon={faIdBadge as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='accounts.details.accountId' /></p>
                <p className={styles.name}>{user.accounts[0]?.friendly_identifier}</p>
              </div>
            </div>}
            <div className={styles.item}>
              <FontAwesomeIcon icon={faUser as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='common.userGroup' /></p>
                <p className={styles.name}>
                  {user.groups?.map((group) => {
                    return (
                      <p key={group.id}>{translate(`groups.${group.name}`) as string}</p>
                    )
                  })}
                </p>
              </div>
            </div>
          </div>
        </button>

        {user?.access_scope !== IAccessScope.system && <button className={styles.userServicesOption} onClick={handleServicesSection}>
          <p className={styles.label}><Translate id='notifications.users.details.services' /></p>
          <div className={styles.items}>
            {Object.values(user.applications)?.map((item: any, index) => {
              return (
                <div className={styles.item} key={index}>
                  <div className={styles.icon}>{serviceIcon(item.name)}</div>
                  <div className={styles.info}>
                    <p className={styles.name}>{item.name} {item.service_provider_accounts && item.service_provider_accounts.length > 0 ? '(' + item.service_provider_accounts.length + ')' : null}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </button>}

        {user.status !== 'DELETED' &&
          <button className={cn(styles.resetCredentialsOption, (user.status === 'PENDING' || isProfileEndUser) && styles.inActive)} onClick={enableResetCredentials}>
          <p className={styles.label}><Translate id='notifications.users.details.userCredentials' /></p>
          <div className={styles.items}>
              {stdSignInEnabled &&
                            <div className={styles.item}>
                                <FontAwesomeIcon icon={faLock as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate
                                        id='accounts.details.signInMethods.passwordAuthentication' /></p>
                                </div>
                            </div>
                        }
                        {mfaEnabled &&
                            <div className={styles.item}>
                                <FontAwesomeIcon icon={faShieldAlt as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate id='accounts.details.signInMethods.2fa' /></p>
                                </div>
                            </div>
                        }
                        {(cacEnabled || cacEnabled1fa) &&
                            <div className={styles.item}>
                                <FontAwesomeIcon icon={faIdBadge as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate id='common.form.clientAuthCert' /></p>
                                </div>
                            </div>
                        }
                        {ssoSamlEnabled &&
                            <div className={styles.item}>
                                <FontAwesomeIcon icon={faShieldAlt as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate id='ssoSaml.singleSignOnWithSaml' /></p>
                                </div>
                            </div>
                        }
                        {oidcEnabled &&
                            <div className={styles.item}>
                                <FontAwesomeIcon icon={faShieldAlt as IconProp} className={styles.icon} />
                                <div className={styles.info}>
                                    <p className={styles.name}><Translate id='common.form.singleSignOnOidc' /></p>
                                </div>
                            </div>
                        }
          </div>
        </button>}
      </div>
      <EditUserSideRail
        user={user}
        initLocalization={initLocalization}
        profile={profile}
        updateDetails={updateDetails}
        showSideRail={showSideRail}
        sideRailContent={sideRailContent}
        setShowSideRail={setShowSideRail} />
      {showDeleteUserModal && (
        <StandardModal
          modalType={ModalTypes.DESTRUCTIVE}
          headline={translate('manageUser.administrators.delete.title') as string}
          buttonLabel={translate('manageUser.administrators.delete.deleteUser') as string}
          buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
          display={showDeleteUserModal}
          onOkCallBack={() => {
            deleteUser([user.id], user.user_name).then(() => {
              setShowDeleteUserModal(false)
              history.push(RouterConfig.administrators.table)
            })
          }}
          onCancelCallBack={() => {
            setShowDeleteUserModal(false)
          }}
        >
          <Translate id='manageUser.administrators.delete.content' />
        </StandardModal>
      )}
      {showUnlockUserModal && (
        <StandardModal
          modalType={ModalTypes.STANDARD}
          headline={translate('unlockUser.header') as string}
          buttonLabel={translate('unlockUser.unlock') as string}
          buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
          display={showUnlockUserModal}
          onOkCallBack={unlockUser}
          onCancelCallBack={() => {
            setShowUnlockUserModal(false)
          }}
        >
          <Translate id='unlockUser.content' />
          <br/><br/>
          <Translate id='unlockUser.content1' />
        </StandardModal>
      )}
    </section>
  )
}

export default UserOverview;
